import { Breadcrumb, Button, Progress } from "antd";
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { formatDate, getSummaryDescription, handleRouteNav, numberFormatter } from "../../utils";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomBadge from "../../components/Charts/CustomBadge";
import { PiShoppingCartFill, PiUserCircle, PiUserCircleFill } from "react-icons/pi";
import { useApiCall } from "../../services/api";
import PrimaryButton from "../../components/Forms/PrimaryButton";
import { useGlobalModal } from "../../context/SuccessModalProvider";

const SchemeDetails = () => {
  const desclimar = [
    "Meeting the quantity threshold alone does not guarantee that the scheme will apply.",
    "The scheme may not apply to all products and orders, and there may be other criteria for it to apply.",
    "Some of the additional criteria for this scheme are mentioned as Special Remarks. Please refer below to know more.",
    "For any clarifications or further information regarding this scheme, please contact your sales representative.",
  ];

  const { role_id, customer_id } = useSelector((state) => state.auth);
  const { setTitle, setDescription, setBackButton } = useOutletContext();
  const [schemeData, setSchemeData] = useState([]);
  const { getApi, postApi, loading } = useApiCall();
  const location = useLocation();
  const { offerId } = location.state || {};
  const scheme_id = offerId;
  const navigate = useNavigate();
  const { showPopup } = useGlobalModal();

  useEffect(() => {
    setTitle("Scheme Details");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            {
              title: "Home",
              href: handleRouteNav("", role_id),
            },
            {
              title: "Scheme Details",
              href: handleRouteNav(`/home/scheme-details`, role_id),
              state: { offerId: scheme_id },
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href} state={item.state}>
                {item.title}
              </Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  const fetchOfferDetails = async () => {
    const payload = {
      customer_id: customer_id,
      offer_id: scheme_id,
    };
    const res = await postApi("offer/get-detail", payload);
    if (res && res.data) {
      setSchemeData(res.data);
    } else if (!res?.status) {
      return showPopup({
        title: null,
        content: <span className="custom-modal-title">{res?.message}</span>,
        onOk: () => {
          navigate(-1);
        },
        className: "custom-success-modal",
      });
    }
  };

  useEffect(() => {
    fetchOfferDetails();
  }, []);

  if (!schemeData?.remarks?.trim()) {
    desclimar.splice(2, 1);
  }

  const sortedVariantSummary =
    [...(schemeData?.variant_summary || [])]
      ?.sort((a, b) => a?.required_quantity - b?.required_quantity)
      .map((a, i) => ({ ...a, index: i + 1 })) || [];
  let currentVariantSummary = sortedVariantSummary?.find((item) => item?.status === "active");

  if (!currentVariantSummary && sortedVariantSummary.every((item) => item?.status == "completed")) {
    currentVariantSummary = sortedVariantSummary?.[sortedVariantSummary?.length - 1];
  } else if (!currentVariantSummary) {
    currentVariantSummary = sortedVariantSummary?.[0];
  }

  return (
    <>
      <>
        <div className="pb-7">
          <div className="bg-[#FFFFFF] leading-normal p-6" style={{ borderRadius: "32px" }}>
            <div className="flex justify-between items-center ">
              <div className="flex gap-4 items-center">
                <div className="bg-[#F4F4F4] p-2 w-fit" style={{ borderRadius: "24px" }}>
                  <img
                    className="min-h-[64px] min-w-[64px]"
                    src="/assets/icons/welcome-logo.png"
                  ></img>
                </div>

                <div className="text-left">
                  <strong className="text-xl text-[#000]">{schemeData?.title}</strong>
                  <div className="text-[#00000080] mt-2">
                    {formatDate(schemeData?.from_date)} - {formatDate(schemeData?.to_date)}
                  </div>
                </div>
              </div>

              <CustomBadge
                variant={
                  schemeData?.status === "Active"
                    ? "success"
                    : schemeData?.status === "Inactive"
                      ? "pending"
                      : "danger"
                }
                text={schemeData?.status}
                divClassName={"me-2"}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-7">
            <div className="bg-[#FFFFFF] leading-normal text-black p-6 rounded-[32px]">
              <div className="text-left">
                <strong className="text-xl text-[#000]">Your Summary</strong>
                <div className="text-[#000] mt-2">
                  {getSummaryDescription(currentVariantSummary, sortedVariantSummary)}
                </div>
              </div>

              <div className="flex gap-3 items-center mt-10">
                <img src="/assets/icons/success-pro.svg" alt="icon" />
                <div className=" text-[#000]">Scheme Progress</div>
              </div>

              <div className="text-left mt-6">
                <div className="text-[#000] mb-2">
                  <b>{numberFormatter(currentVariantSummary?.achieved_quantity)}</b> out of{" "}
                  <b>{numberFormatter(currentVariantSummary?.required_quantity)}</b>{" "}
                  {currentVariantSummary?.quantity_type === "Cases" ? "cases" : "meters"}
                </div>

                <Progress
                  percent={currentVariantSummary?.progress_percent}
                  showInfo={false}
                  strokeWidth={16}
                  strokeColor="#a33434"
                  trailColor="#A3343426"
                  className={
                    currentVariantSummary?.progress_percent >= 100 ? "glow-progress-full" : ""
                  }
                />
              </div>

              <div className="text-left mt-4">
                <strong className="text-xl text-[#000]">Slab {currentVariantSummary?.index}</strong>

                <div className="flex flex-col mt-4 gap-3">
                  <div className="flex gap-3 items-center">
                    <img src="/assets/icons/quantity.svg" alt="icon" />
                    <div className="">
                      <div className="text-[#00000080] ">Quantity</div>
                      <strong className="text-lg text-[#000] mt-2">
                        {numberFormatter(currentVariantSummary?.required_quantity)}{" "}
                        {currentVariantSummary?.quantity_type === "Cases" ? "Cases" : "Mtrs"}
                      </strong>
                    </div>
                  </div>

                  <div className="flex gap-3 items-center">
                    <img src="/assets/icons/incentive.svg" alt="icon" />
                    <div className="">
                      <div className="text-[#00000080] ">Incentive</div>
                      <strong className="text-lg text-[#000] mt-2">
                        {currentVariantSummary?.credit_value}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-md font-bold text-[#000]">Disclaimers</h2>
                </div>

                <div className="space-y-4">
                  {desclimar.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-2 h-2 bg-[#A33434] rounded-full"></div>
                      </div>
                      <p className="flex-1 text-left text-gray-600 leading-relaxed text-sm">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative h-[600px] md:h-auto rounded-[32px]">
              <div className="md:absolute inset-0 bg-[#FFFFFF] flex flex-col p-6 rounded-[32px] h-full overflow-hidden">
                <div className="flex justify-between items-center mb-4 flex-none">
                  <div className="text-left">
                    <strong className="text-xl text-[#000]">Products</strong>
                  </div>
                  <div>
                    <Button
                      type="primary"
                      className="h-[45px] bg-[#7070701A] hover:!bg-[#7070701A] hover:!text-[#A33434] font-bold text-[#A33434] text-sm sm:text-base whitespace-nowrap !cursor-default"
                      size="middle"
                    >
                      {schemeData?.product_details?.length || 0} products
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-2">
                  {schemeData?.product_details?.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center gap-3 mt-6 pb-6 border-b border-[#DDDDDD] last:border-b-0"
                    >
                      <div>
                        <div className="flex justify-between items-center gap-3">
                          <img
                            className="h-[88px] w-[88px] shrink-0"
                            style={{ borderRadius: "16px" }}
                            src={item?.product?.product_pic_url}
                            alt="product"
                          ></img>

                          <div>
                            <div className="text-left">
                              <strong className="text-xl text-[#000]">{item?.short_name}</strong>
                              <div className="text-[#979797] flex items-center gap-2 mt-2">
                                {item?.clienttype} | {item?.fabric_type}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <PrimaryButton
                          text="Order Now"
                          icon={<PiShoppingCartFill className="text-[#ffff]" />}
                          onClick={() =>
                            navigate(
                              handleRouteNav(`/product-details/${item?.product?.sort_no}`, role_id),
                            )
                          }
                          iconOnleft
                          btnType="ghost"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FFFFFF] flex flex-col mt-7 p-6 rounded-[32px] overflow-hidden">
            <div className="text-left">
              <strong className="text-xl text-[#000]">Scheme Information</strong>
            </div>
            <div className="">
              {schemeData?.variants?.map((item, index) => (
                <>
                  <div>
                    <div className="text-left mt-6">
                      <strong className="text-xl text-[#000]">Slab {index + 1}</strong>
                      <div className="grid grid-cols-2 md:grid-cols-2 gap-6  mt-4">
                        <div className="flex gap-3 items-center">
                          <img src="/assets/icons/quantity.svg"></img>
                          <div className="">
                            <div className="text-[#00000080] ">Quantity</div>
                            <strong className="text-lg text-[#000] mt-2">
                              {numberFormatter(item?.variant_qty)}{" "}
                              {schemeData?.qty_type === "Meter"
                                ? "Mtrs"
                                : schemeData?.qty_type || "Cases"}
                            </strong>
                          </div>
                        </div>

                        <div className="flex gap-3 items-center">
                          <img src="/assets/icons/incentive.svg"></img>
                          <div className="">
                            <div className="text-[#00000080] ">Incentive</div>
                            <strong className="text-lg text-[#000] mt-2 capitalize">
                              {item?.variant_incentive}{" "}
                              {schemeData?.credit_type == "percentage" ? "%" : "Rs. / Mtr"}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ))}
            </div>
          </div>

          {schemeData?.remarks?.trim() && (
            <div className="bg-[#FFFFFF] leading-normal p-6 mt-7" style={{ borderRadius: "32px" }}>
              <div className="text-left flex gap-3 items-center">
                <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                  <PiUserCircle size={24} fill="#A33434" />
                </div>
                <div>
                  <strong className="text-xl text-[#000]">Special Remarks</strong>
                </div>
              </div>

              <div className="space-y-4 mt-8 ps-4 text-[#000] text-start">
                <span className="flex whitespace-pre-line">
                  {schemeData?.remarks?.replace(/\\n/g, "\n")}
                </span>
              </div>
            </div>
          )}
        </div>
      </>
    </>
  );
};

export default SchemeDetails;
